import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { listPresentations, createPresentation } from '../../services/presentationsService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiPlay, FiEdit, FiSliders } = FiIcons;

export default function PresentationsTab({ eventId }) {
  const { profile } = useAuth();
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const opts = profile?.id && profile?.role ? { role: profile.role, userId: profile.id } : {};

  useEffect(() => {
    if (!eventId || !profile?.id) return;
    let cancelled = false;
    listPresentations({ ...opts, eventId }).then(({ data, error }) => {
      if (!cancelled) {
        setPresentations(data || []);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [eventId, profile?.id, opts.role]);

  const handleCreate = async () => {
    setCreating(true);
    const { data, error } = await createPresentation(
      { title: 'Untitled Presentation', type: 'slides', event_id: eventId },
      opts
    );
    setCreating(false);
    if (data?.id) {
      setPresentations((prev) => [...prev, data]);
    }
  };

  if (!profile?.id || (profile.role !== 'agent' && profile.role !== 'admin')) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Presentations</h2>
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="inline-flex items-center px-3 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0090dd] disabled:opacity-50 text-sm"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          {creating ? 'Creating…' : 'New presentation'}
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Presentations linked to this event. Create one here or attach from the main Presentations list.
      </p>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#00AAFF] border-t-transparent" />
        </div>
      ) : presentations.length === 0 ? (
        <div className="text-center py-8 text-gray-500 rounded-lg border border-dashed border-gray-300">
          No presentations linked to this event yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {presentations.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900 truncate">{p.title || 'Untitled'}</span>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <Link
                  to={`/presentations/${p.id}/view`}
                  className="p-2 text-gray-500 hover:text-[#00AAFF] rounded"
                  title="View"
                >
                  <SafeIcon icon={FiPlay} className="w-4 h-4" />
                </Link>
                <Link
                  to={`/presentations/${p.id}/edit`}
                  className="p-2 text-gray-500 hover:text-[#00AAFF] rounded"
                  title="Edit"
                >
                  <SafeIcon icon={FiEdit} className="w-4 h-4" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4">
        <Link
          to="/presentations"
          className="inline-flex items-center text-sm text-[#00AAFF] hover:underline"
        >
          <SafeIcon icon={FiSliders} className="w-4 h-4 mr-2" />
          Open all presentations
        </Link>
      </div>
    </div>
  );
}
